// Shipping country list. Ireland is the domestic destination; everything else
// is international and routed through admin-defined shipping zones.

export const DOMESTIC_COUNTRY = { code: 'IE', name: 'Ireland' };

// EU member states (excluding Ireland) available for international shipping.
export const INTERNATIONAL_COUNTRIES = [
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
];

// Full list (Ireland first) for the checkout country dropdown.
export const ALL_COUNTRIES = [DOMESTIC_COUNTRY, ...INTERNATIONAL_COUNTRIES];

export const isDomesticCountry = (code) => !code || code === 'IE';

export const countryName = (code) => {
  if (!code) return '';
  const match = ALL_COUNTRIES.find((c) => c.code === code);
  return match ? match.name : code;
};

// Find which admin zone (if any) contains a given country code.
export const findZoneForCountry = (zones, code) => {
  if (!code || !Array.isArray(zones)) return null;
  return zones.find((z) => (z.countries || []).includes(code)) || null;
};

// ---------------------------------------------------------------------------
// Live-animal classification + split rate cards + cold-weather HeatPack.
// Mirrors backend services/shipping.py exactly (the backend is authoritative).
// ---------------------------------------------------------------------------
export const HEATPACK_LABEL = '48 Hour HeatPack';

const LIVE_RATE_DEFAULTS = { under_100: 14.9, '100_to_200': 7.5, '200_plus': 0 };
const FOOD_RATE_DEFAULTS = { under_100: 6.5, '100_to_200': 4.5, '200_plus': 0 };

// A cart/order line contains live animals when it is livestock, or a jar/aquarium
// with shrimp selected inside it. Food & dry goods ("simple") never do.
export const isLiveAnimalItem = (item) => {
  const type = item?.product_type || 'livestock';
  if (type === 'livestock' || type === 'shrimp') return true;
  if (type === 'item' || type === 'jar') {
    const sel = item?.jar_config?.shrimp_selections || item?.jar_config?.shrimp || [];
    return sel.some((s) => (parseInt(s?.quantity, 10) || 0) > 0);
  }
  return false;
};

// 'live' (thermal box) when any line has live animals, else 'standard' (food only).
export const shippingCategoryFor = (items) =>
  (items || []).some(isLiveAnimalItem) ? 'live' : 'standard';

// The 3-tier domestic rate card for a category, with defaults for unset keys.
export const domesticRateCard = (settings, category = 'live') => {
  const s = settings || {};
  const prefix = category === 'standard' ? 'food_' : '';
  const defaults = category === 'standard' ? FOOD_RATE_DEFAULTS : LIVE_RATE_DEFAULTS;
  const read = (key) => {
    const v = parseFloat(s[`${prefix}shipping_rate_${key}`]);
    return Number.isFinite(v) ? v : defaults[key];
  };
  return { under_100: read('under_100'), '100_to_200': read('100_to_200'), '200_plus': read('200_plus') };
};

export const tierKeyFor = (itemsTotal) =>
  itemsTotal >= 200 ? '200_plus' : itemsTotal >= 100 ? '100_to_200' : 'under_100';

export const tierLabelFor = (itemsTotal) =>
  itemsTotal >= 200 ? '\u20AC200+' : itemsTotal >= 100 ? '\u20AC100\u2013\u20AC199' : 'Under \u20AC100';

export const domesticTierFee = (settings, itemsTotal, category = 'live') =>
  domesticRateCard(settings, category)[tierKeyFor(itemsTotal)];

// Mandatory HeatPack on shipped live-animal orders when the admin switch is ON.
// Waived when the shipping fee itself is free (€0).
export const heatpackFeeFor = (settings, category, shippingFee, deliveryMethod = 'shipping') => {
  if (deliveryMethod !== 'shipping' || category !== 'live') return 0;
  if (!settings?.heatpack_enabled) return 0;
  if (!(parseFloat(shippingFee) > 0)) return 0;
  const p = parseFloat(settings.heatpack_price);
  return Number.isFinite(p) ? p : 5;
};

// Zone-aware shipping preview for an order (admin editor + storefront parity).
// Mirrors backend services/shipping.compute_shipping_charges exactly:
//   • Ireland / empty  -> domestic tiered rates for the order's category
//   • any other country -> the matched enabled zone's flat price, else not shippable
//   • heatpack          -> added for live-animal orders when enabled & shipping > 0
// Returns { applicable, fee, heatpack, category, domestic, shippable, zone, tierLabel }.
export const computeOrderShipping = (order, settings) => {
  const s = settings || {};
  const category = shippingCategoryFor(order?.items);
  if (!order || order.delivery_method !== 'shipping') {
    return { applicable: false, fee: 0, heatpack: 0, category, domestic: true, shippable: true, zone: null, tierLabel: null };
  }
  const itemsTotal = (order.items || []).reduce((sum, i) => sum + (parseFloat(i.total_price) || 0), 0);
  const country = order.customer_country || 'IE';
  if (isDomesticCountry(country)) {
    const fee = domesticTierFee(s, itemsTotal, category);
    const heatpack = heatpackFeeFor(s, category, fee);
    return { applicable: true, fee, heatpack, category, domestic: true, shippable: true, zone: null, tierLabel: tierLabelFor(itemsTotal) };
  }
  const zone = findZoneForCountry(s.intl_shipping_zones, country);
  const shippable = !!s.intl_shipping_enabled && !!zone;
  const fee = shippable ? (parseFloat(zone.price) || 0) : 0;
  const heatpack = heatpackFeeFor(s, category, fee);
  return { applicable: true, fee, heatpack, category, domestic: false, shippable, zone, tierLabel: null };
};
